import { localizedStrings } from '@core/strings'
import React, { useMemo } from 'react'
import { MenuItem, TextField, Card } from '@common/material'
import { TextFieldProps } from '@mui/material/TextField/TextField'
import { ChecklistTemplateRole } from '@core/entities/checklist/ChecklistTemplateRoles'
import { Section } from '@common/Section'
import { Text } from '@fundamentals'
import { useGetChecklistRoles } from '@core/react-query/features/checklists/checklist'
import { ProfileGroup } from '@core/entities/profile/types'

export type ChecklistRolePickerProps = TextFieldProps & {
  readOnly?: boolean
  checklistId: number
  profileGroups: ProfileGroup[]
  accessToken: string
  value: Pick<ChecklistTemplateRole, 'id' | 'name'>
  onChange?: (role: ChecklistTemplateRole) => void
}

const ChecklistRolePicker: React.FC<ChecklistRolePickerProps> = ({
  checklistId,
  profileGroups,
  readOnly,
  accessToken,
  value,
  onChange,
  ...props
}) => {
  const { data: allRoles, isLoading } = useGetChecklistRoles(
    { checklistId, accessToken },
    { enabled: !!checklistId && !readOnly },
  )

  const roles = useMemo(
    () =>
      allRoles?.filter(
        (role) =>
          !role.groupIds?.length ||
          role.groupIds.some((groupId) =>
            profileGroups.some((group) => group.id === groupId),
          ),
      ),
    [allRoles, profileGroups],
  )

  const noEligibleRoles = !isLoading && !roles?.length

  const findRoleById = (id) => roles?.find((role) => role.id === id)

  if (readOnly) {
    return (
      <Card sx={{ p: 2 }}>
        <Section label={localizedStrings.role}>
          <Text>{value?.name}</Text>
        </Section>
      </Card>
    )
  }

  return (
    <TextField
      value={value?.id ?? ''}
      label={
        noEligibleRoles
          ? localizedStrings.noEligibleRolesForBriefingFound
          : localizedStrings.role
      }
      disabled={noEligibleRoles}
      data-test='select-role-dropdown-btn'
      placeholder={localizedStrings.selectRole}
      onChange={({ target: { value: roleId } }) => {
        onChange(findRoleById(roleId))
      }}
      select
      required
      {...props}
    >
      {roles?.map((role, index) => {
        return (
          <MenuItem
            key={role.id}
            value={role.id}
            data-test={`select-role-dropdown-option-${index}`}
          >
            {role.name}
          </MenuItem>
        )
      })}
    </TextField>
  )
}

export { ChecklistRolePicker }
